import { useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import { Breadcrumb, Divider, Flex, Link, Text } from "@/atoms"
import { NAMES_ROUTES, VALUES_PARAMS } from "@/const/routes.constants"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { AlignLeftOutlined, FileProtectOutlined, StopOutlined } from "@/icons"
import { Heading } from "@/molecules/Heading/Heading"
import { Form } from "@/organisms/Form"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { getDecisionsUrl } from "@/pages/GovernancePage/components/pages/AiPoliciesPage/const"
import { ENFORCEMENT_SECTION_ID } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/EnforcementSection"
import { RuleCardList } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleCardList"
import { SummarySection } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/SummarySection"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { LiveAiPolicyForm } from "./components/LiveAiPolicyForm"
import { LiveEnforcementSection } from "./components/LiveEnforcementSection"

import styles from "@/pages/GovernancePage/components/pages/AiPolicyPage/AiPolicyPage.module.scss"

import type { AiPolicyFormValues } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"

// A real AI policy, to view or edit: the demo keeps its own page
export const LiveAiPolicyPage = () => {
  const { t } = useTranslation("translation")
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle"
  )
  const { response, mode, isPending, live } = useAiPolicies()
  const { ROOT, AI } = NAMES_ROUTES.GOVERNANCE
  const listUrl = `/${ROOT}/${AI}`
  const isNew = id === VALUES_PARAMS.NEW_ENTITY
  const record = response?.data?.[0]
  const isMcpServer = live?.kind === "mcpServer"

  if (record?.type === "heading") return null

  const { Details, Resources } = Form.Section
  const enforcementTitle = "governance.ai.live.form.enforcement.title"
  const asideItems = [
    { key: Details.sectionId, tKey: "AI Policy Details" },
    {
      key: Resources.sectionId,
      tKey: isMcpServer
        ? "governance.ai.live.form.mcpResources.title"
        : "governance.ai.form.resources.title"
    },
    { key: "rules", tKey: "Rules" },
    { key: ENFORCEMENT_SECTION_ID, tKey: enforcementTitle }
  ]
  const headerItems = [
    { link: ROOT, tKey: "navigation.governance" },
    { link: listUrl, tKey: "AI Policy" },
    {
      fieldName: "name_details",
      title: record?.policyName,
      tKey: isMcpServer
        ? "governance.ai.live.kinds.mcpServer.create"
        : "Create AI Policy"
    }
  ]
  const rootUrl = record ? `${listUrl}/${record.id}` : listUrl
  const cancelRedirect = record && !isNew ? `${rootUrl}/view` : listUrl

  // A policy that is gone is not edited as a new one
  if (!isNew && !isPending && !record) return <Navigate replace to={listUrl} />

  if (mode === "edit")
    return (
      <Form.Page
        loading={isPending}
        id="ai-policy-form"
        aside={<Form.Aside items={asideItems} followScroll />}
        header={<Form.Header items={headerItems} />}
        footer={
          <Form.Footer
            submitting={saveState === "saving"}
            cancelRedirect={cancelRedirect}
            okText={(isNewEntity) =>
              isNewEntity
                ? t("governance.ai.form.buttons.apply")
                : t("general.saveUpdate")
            }
            saveGuard={(isNewEntity, values: AiPolicyFormValues) => {
              const name = values.name_details
              const key = isNewEntity ? "created" : "updated"

              return {
                content: (
                  <Trans>
                    {t(`governance.ai.form.messages.guard.${key}`, { name })}
                  </Trans>
                )
              }
            }}
          />
        }
      >
        <LiveAiPolicyForm
          record={record}
          redirectUrl={listUrl}
          setUpdating={(updating) =>
            setSaveState(updating ? "saving" : "saved")
          }
        />
      </Form.Page>
    )

  if (!record) return isPending ? null : <Navigate replace to={listUrl} />

  const { policyName, policyDescription } = record

  return (
    <PageWithBreadcrumbTemplate
      full
      loading={isPending}
      header={
        <Breadcrumb
          items={headerItems.map((item) => {
            const { tKey = "", link, title = t(tKey) } = item

            if (link) return { title: <Link to={link}>{title}</Link> }

            return { title: item.title }
          })}
        />
      }
    >
      <Flex vertical>
        <Flex gap={10} align="center" className={styles.sectionTitle}>
          <AlignLeftOutlined className={styles.sectionIcon} />
          <Heading title="AI Policy Details" level={4} />
        </Flex>
        <Divider className={styles.divider16} />
        <SummarySection
          record={record}
          onClick={() => navigate(`${rootUrl}/edit`)}
          onViewDecisions={() => navigate(getDecisionsUrl(`${record.id}`))}
        />
        <Divider className={styles.divider16} />
        <Flex vertical gap={12}>
          <Flex vertical>
            <Text type="secondary" className="sm-text-ks">
              AI Policy Name
            </Text>
            <Text>{policyName}</Text>
          </Flex>
          <Flex vertical>
            <Text type="secondary" className="sm-text-ks">
              Description
            </Text>
            <Text>{policyDescription}</Text>
          </Flex>
        </Flex>
        <Flex gap={10} align="center" className={styles.sectionTitle}>
          <FileProtectOutlined className={styles.sectionIcon} />
          <Heading title="Rules" level={4} />
        </Flex>
        <Divider className={styles.divider16} />
        <RuleCardList record={record} />
        {record.live && (
          <>
            <Flex
              gap={10}
              align="center"
              className={styles.sectionTitle}
              id={ENFORCEMENT_SECTION_ID}
            >
              <StopOutlined className={styles.sectionIcon} />
              <Heading title={t(enforcementTitle)} level={4} />
            </Flex>
            <Divider className={styles.divider16} />
            <LiveEnforcementSection policy={record.live} />
          </>
        )}
      </Flex>
    </PageWithBreadcrumbTemplate>
  )
}
